import { useLocaleState, useTranslate } from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  OBJECTION_MAX,
  RESPONSE_MAX,
  fromObjectionForm,
  tradeOptions,
  validateObjectionForm,
  type ObjectionData,
  type ObjectionForm,
  type ObjectionFormErrors,
} from "./objectionModel";

export const SELECT_CLASS =
  "h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs focus-visible:outline-2 focus-visible:outline-primary";

type Props = {
  mode: "create" | "edit";
  initial: ObjectionForm;
  onCancel: () => void;
  /** Resolves when saved; rejects to keep the dialog open. */
  onSave: (data: ObjectionData) => Promise<void>;
};

/** GAVION X2: create / edit dialog of an objection (admins only). */
export const ObjectionEditor = ({ mode, initial, onCancel, onSave }: Props) => {
  const t = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const [form, setForm] = useState<ObjectionForm>(initial);
  const [errors, setErrors] = useState<ObjectionFormErrors>({});
  const [saving, setSaving] = useState(false);
  const set = <K extends keyof ObjectionForm>(k: K, v: ObjectionForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  const err = (k: keyof ObjectionFormErrors) =>
    errors[k] && (
      <p id={`obj-${k}-err`} role="alert" className="text-sm text-destructive">
        {t(`gavion.objections.settings.errors.${errors[k]}`)}
      </p>
    );
  const aria = (k: keyof ObjectionFormErrors) => ({
    "aria-invalid": !!errors[k],
    "aria-describedby": errors[k] ? `obj-${k}-err` : undefined,
  });

  const save = async () => {
    const e = validateObjectionForm(form);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      await onSave(fromObjectionForm(form));
    } catch {
      /* the page notifies; keep the dialog open */
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {t(
              mode === "create"
                ? "gavion.objections.settings.dialog.create_title"
                : "gavion.objections.settings.dialog.edit_title",
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("gavion.objections.settings.intro")}
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <div className="flex flex-col gap-1">
            <Label htmlFor="obj-trade">
              {t("gavion.objections.settings.dialog.trade")}
            </Label>
            <select
              id="obj-trade"
              className={SELECT_CLASS}
              value={form.trade}
              onChange={(e) => set("trade", e.target.value)}
            >
              <option value="">
                {t("gavion.objections.settings.generic")}
              </option>
              {tradeOptions(locale).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
              {form.trade &&
                !tradeOptions(locale).some((o) => o.value === form.trade) && (
                  <option value={form.trade}>{form.trade}</option>
                )}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="obj-objection">
              {t("gavion.objections.settings.dialog.objection")}
            </Label>
            <Input
              id="obj-objection"
              maxLength={OBJECTION_MAX + 50}
              value={form.objection}
              onChange={(e) => set("objection", e.target.value)}
              {...aria("objection")}
            />
            {err("objection")}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="obj-response">
              {t("gavion.objections.settings.dialog.response")}
            </Label>
            <Textarea
              id="obj-response"
              rows={6}
              maxLength={RESPONSE_MAX + 100}
              value={form.response}
              onChange={(e) => set("response", e.target.value)}
              {...aria("response")}
            />
            {err("response")}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_140px]">
            <div className="flex flex-col gap-1">
              <Label htmlFor="obj-tags">
                {t("gavion.objections.settings.dialog.tags")}
              </Label>
              <Input
                id="obj-tags"
                value={form.tags}
                onChange={(e) => set("tags", e.target.value)}
                {...aria("tags")}
              />
              {err("tags")}
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="obj-order">
                {t("gavion.objections.settings.dialog.sort_order")}
              </Label>
              <Input
                id="obj-order"
                inputMode="numeric"
                value={form.sort_order}
                onChange={(e) => set("sort_order", e.target.value)}
                {...aria("sort_order")}
              />
              {err("sort_order")}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="obj-active"
              checked={form.active}
              onCheckedChange={(v) => set("active", v)}
            />
            <Label htmlFor="obj-active">
              {t("gavion.objections.settings.dialog.active")}
            </Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>
              {t("gavion.objections.settings.dialog.cancel")}
            </Button>
            <Button type="submit" disabled={saving}>
              {t("gavion.objections.settings.dialog.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
